import { Box, makeStyles } from '@material-ui/core';
import { ReactNode } from 'react';

import { getPlatformFieldName } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/constants/platformFieldDomainRef';
import { PlatformCredentialField } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/types/CredentialFieldConfig';
import { Font } from '@/components/shared/components/Font';
import { StyledTooltip } from '@/components/shared/components/StyledTooltip';
import { Text } from '@/components/shared/components/Text';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    minWidth: 0,
    maxWidth: '100%',
    whiteSpace: 'nowrap',
  },
  labelText: {
    flexShrink: 0,
    lineHeight: '1.1rem',
  },
  platformField: {
    display: 'block',
    flex: '0 1 auto',
    minWidth: 0,
    maxWidth: 240,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: 'Google Sans Code, monospace',
    fontSize: theme.typography.subtitle2.fontSize,
    fontWeight: 400,
    lineHeight: 1.2,
    color: theme.palette.grey[500],
    backgroundColor: theme.palette.grey[100],
    borderRadius: 6,
    padding: '2px 6px',
    marginLeft: 4,
    marginRight: 2,
    verticalAlign: 'middle',
  },
  info: {
    flexShrink: 0,
    fontSize: 16,
    lineHeight: 1,
    color: theme.palette.grey[400],
    marginLeft: 4,
    cursor: 'help',
  },
  optional: {
    flexShrink: 0,
    opacity: 0.5,
    paddingLeft: '0.5em',
  },
}));

interface CredentialFieldLabelProps {
  label: string;
  platformField: PlatformCredentialField;
  disableTranslation?: boolean;
  markAsOptional?: boolean;
  tooltip?: string;
}

export const CredentialFieldLabel = ({
  label,
  platformField,
  disableTranslation = false,
  markAsOptional = false,
  tooltip,
}: CredentialFieldLabelProps) => {
  const classes = useStyles();
  const platformFieldName = getPlatformFieldName(platformField);

  const labelContent: ReactNode = disableTranslation ? label : <Text tId={label} />;

  return (
    <Box className={classes.root}>
      <Font variant="subtitle2" color="textSecondary" className={classes.labelText}>
        {labelContent}
      </Font>
      {tooltip && (
        <StyledTooltip title={tooltip} tooltipProps={{ placement: 'top' }}>
          <i className={`material-icons-outlined ${classes.info}`}>info</i>
        </StyledTooltip>
      )}
      <StyledTooltip title={platformFieldName} tooltipProps={{ placement: 'top' }}>
        <Box component="span" className={classes.platformField}>
          {platformFieldName}
        </Box>
      </StyledTooltip>
      {markAsOptional && (
        <span className={classes.optional}>
          {'('}
          <Text tId="OPTIONAL" />
          {')'}
        </span>
      )}
    </Box>
  );
};
